import { create } from "zustand";
import { configurePersist } from "zustand-persist";
import { AccelerateVestingDto } from "../types/Grant";

const { persist: _persist } = configurePersist({
  storage: sessionStorage,
  rootKey: "root",
});

const persist = _persist as any;

export interface AccelerateStoreData {
  enabled: boolean;
  entries: Record<string, AccelerateVestingDto>;
}

interface AccelerateStore {
  data: Record<string, AccelerateStoreData>;
  getAccelerateData: (employeeId: string) => AccelerateStoreData;
  setAccelerateData: (employeeId: string, data: AccelerateStoreData) => void;
  addOrUpdateEntry: (employeeId: string, entry: AccelerateVestingDto) => void;
  removeEntry: (employeeId: string, grantId: string) => void;
  getAllEntries: (employeeId: string) => AccelerateVestingDto[];
  setEnabled: (employeeId: string, enabled: boolean) => void;
  accelerateAll: (
    employeeId: string,
    submitFn: (
      payload: {
        grantId: string;
        numberOfOptionsToBeAccelerated: number;
        accelerateDate: string;
      }[]
    ) => Promise<any>
  ) => Promise<void>;
  clearAccelerateData: (employeeId: string) => void;
  removeAccelerateData: (employeeId: string) => void;
}

const defaultAccelerateData: AccelerateStoreData = {
  enabled: false,
  entries: {},
};

const accelerateStartedData: AccelerateStoreData = {
  enabled: true,
  entries: {},
};

export const useAccelerateStore = create<AccelerateStore>(
  persist(
    {
      key: "accelerate_store_v1",
    },
    (set: any, get: any) => ({
      data: {},

      getAccelerateData: (employeeId: string) => {
        const state = get();
        const d = state.data[employeeId];
        if (!d)
          return {
            enabled: defaultAccelerateData.enabled,
            entries: { ...defaultAccelerateData.entries },
          };
        return {
          enabled: Boolean(d.enabled),
          entries: { ...(d.entries || {}) },
        };
      },

      setAccelerateData: (employeeId: string, data: AccelerateStoreData) => {
        set((state: AccelerateStore) => ({
          ...state,
          data: {
            ...state.data,
            [employeeId]: data,
          },
        }));
      },

      addOrUpdateEntry: (employeeId: string, entry: AccelerateVestingDto) => {
        set((state: AccelerateStore) => {
          const currentData = state.data[employeeId] || {
            enabled: false,
            entries: {},
          };
          return {
            ...state,
            data: {
              ...state.data,
              [employeeId]: {
                ...currentData,
                entries: {
                  ...currentData.entries,
                  [entry.grantId]: entry,
                },
              },
            },
          };
        });
      },

      getAllEntries: (employeeId: string) => {
        const state = get();
        const employeeData = state.data[employeeId] || {
          enabled: false,
          entries: {},
        };
        return Object.values(employeeData.entries || {});
      },

      setEnabled: (employeeId: string, enabled: boolean) => {
        set((state: AccelerateStore) => {
          const currentData = state.data[employeeId] || {
            enabled: false,
            entries: {},
          };
          return {
            ...state,
            data: {
              ...state.data,
              [employeeId]: {
                ...currentData,
                enabled: Boolean(enabled),
              },
            },
          };
        });
      },
      removeEntry: (employeeId: string, grantId: string) => {
        set((state: AccelerateStore) => {
          const currentData = state.data[employeeId] || {
            enabled: false,
            entries: {},
          };
          const newEntries = { ...currentData.entries };
          delete newEntries[grantId];

          return {
            ...state,
            data: {
              ...state.data,
              [employeeId]: {
                ...currentData,
                entries: newEntries,
              },
            },
          };
        });
      },

      clearAccelerateData: (employeeId: string) => {
        set((state: AccelerateStore) => ({
          ...state,
          data: {
            ...state.data,
            [employeeId]: accelerateStartedData,
          },
        }));
      },

      removeAccelerateData: (employeeId: string) => {
        set((state: AccelerateStore) => {
          const newData = { ...state.data };
          delete newData[employeeId];
          return {
            ...state,
            data: newData,
          };
        });
      },
    })
  )
);
